<template>
  <div ref="mapContainer" class="provincial-map"> 
    <div class="provincial-map__legenda">
      <div v-for="(party, key) in activeParties" :key="key" class="provincial-map__legenda-item">
        <span :style="{backgroundColor: party}" class="legenda-color"/><span class="legenda-label">{{ key }}</span>
      </div>
    </div>
    <components ref="province" :is="getProvinceSetting.mapComponent" />
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { paintOptions } from "../services/preferences.js";

// import all the maps available
import NoordHolland from "./maps/NoordHolland.vue";
import Utrecht from "./maps/Utrecht.vue";
import Drenthe from "./maps/Drenthe.vue";
import Flevoland from "./maps/Flevoland.vue";
import Friesland from "./maps/Friesland.vue";
import Gelderland from "./maps/Gelderland.vue";
import Groningen from "./maps/Groningen.vue";
import Limburg from "./maps/Limburg.vue";
import Overijssel from "./maps/Overijssel.vue";
import Zeeland from "./maps/Zeeland.vue";
import ZuidHolland from "./maps/ZuidHolland.vue";
import NoordBrabant from "./maps/NoordBrabant.vue";

import Vue from "vue";
export default {
  name: "ProvincialMaps",
  components: {
    NoordHolland,
    Utrecht,
    Drenthe,
    Flevoland,
    Friesland,
    Gelderland,
    Groningen,
    Limburg,
    Overijssel,
    Zeeland,
    ZuidHolland,
    NoordBrabant
  },
  data() {
    return {
      activeParties: {}
    };
  },
  computed: {
    ...mapState("feed", ["communities", "selectedDistrict"]),
    ...mapGetters("feed", ["getProvinceSetting"]),
    districtObjects() {
      return this.communities[this.getProvinceSetting.key];
    },
    mapElem() {
      if (this.$refs.province) {
        return this.$refs.province.$el;
      }
      return null;
    }
  },
  watch: {
    selectedDistrict: {
      handler() {
        this.updateCommunities();
      }
    }
  },
  async mounted() {
    if (this.$refs.province) {
      this.updateCommunities({ create: true });
    }
  },
  methods: {
    updateCommunities(settings = {}) {
      if (!this.mapElem) {
        return;
      }
      const img = this.mapElem;
      const communities = Array.from(img.children).filter(
        el => el.nodeName !== "symbol" // collect all children from the svg except the one with a 'symbol' element
      );
      const originalColors = {};
      for (const single of communities) {
        this.paintCommunity(single);
        originalColors[single.id] = single.style.fill;
        const cbs = single.id.replace("G", "");

        if (!this.districtObjects[cbs]) {
          continue;
        }
        if (settings.create) {
          // only apply click event when the create setting is true.
          // We don't want the event to stack with each update
          single.addEventListener("click", e => {
            const id = e.target.id || e.target.parentElement.id;
            this.$emit("onselect", id);
          });

          // create tooltip for each district
          const tooltip = document.createElement("div");
          tooltip.setAttribute("id", cbs);
          tooltip.classList.add("provincial-map__tooltip");
          tooltip.innerHTML = this.districtObjects[cbs].title._text;
          this.$refs.mapContainer.appendChild(tooltip);
          tooltip.style.position = "absolute";

          single.addEventListener("mousemove", e => {
            tooltip.style.display = "block";
            tooltip.style.top = e.layerY - 50 + "px";
            tooltip.style.left = e.layerX - 20 + "px";
          });
          single.addEventListener("mouseout", () => {
            tooltip.style.display = "none";
          });
        }
      }
    },
    paintCommunity(el) {
      const code = el.id.replace("G", "");
      if (this.districtObjects[code]) {
        const party =
          this.districtObjects[code]["media:grootste"]._attributes.cur ||
          "Geen uitslag";
        Vue.set(
          this.activeParties,
          party,
          paintOptions(party, { activeColor: true })
        );
        // set all to active color if no district has been selected
        const appliedColor = paintOptions(
          party,
          !this.selectedDistrict.CBS && { activeColor: true }
        );
        if (el.children.length) {
          for (const child of el.children) {
            child.style.fill = appliedColor;
          }
        } else {
          el.style.fill = appliedColor;
        }
      }

      el.style.stroke = 0;

      if (
        this.selectedDistrict.CBS &&
        this.selectedDistrict.CBS._text === code
      ) {
        const party = this.districtObjects[this.selectedDistrict.CBS._text][
          "media:grootste"
        ]._attributes.cur;
        if (el.children.length) {
          for (const child of el.children) {
            child.style.fill = paintOptions(party, { activeColor: true });
          }
        } else {
          el.style.fill = paintOptions(party, { activeColor: true });
        }
      }
    }
  }
};
</script>

<style lang="scss">
@import "../util.scss";

.provincial-map {
  position: relative;
  margin: 26px 0;
  &__legenda {
    left: 20px;
    top: 0px;
    position: absolute;
    display: flex;
    flex-wrap: wrap;
    width: 100%;
  }
  &__tooltip {
    position: absolute;
    display: none;
    padding: 12px;
    border-radius: 6px;
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    max-height: 42px;
    @include truncate();
  }
  &__legenda-item {
    width: calc(25% - 8px);
    max-width: 25%;
    display: flex;
    align-items: center;
    margin: 2px 8px 0 0;
    .legenda-color {
      width: 30px;
      height: 7px;
      margin-right: 8px;
      display: block;
    }
    .legenda-label {
      font-size: 11px;
      @include truncate();
    }
  }
  svg {
    margin: 0 auto;
    display: block;
    padding-top: 80px;
    max-height: calc(100vh - 210px);
    path,
    g {
      transition: 200ms ease-in-out all;
      cursor: pointer;
    }
  }
}
</style>
